import { useEventCallback } from '@cian/react-utils';
import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SaveSearchButtonContainer as SaveSearchButton } from './SaveSearchButton';
import { trackAdvancedFiltersClick, trackApplyClick, trackClearClick } from './tracking';
import { Filters, IOnChangeParameters, Provider, TModifier } from '../../../common/packages/Filters';
import { NonEmptyArray } from '../../../common/packages/JsonQuery';
import { IJsonQuery } from '../../../common/packages/api-models/common/json_query';
import { changeJsonQuery, fetchTags } from '../../actions/filters';
import { fetchNewbuildingPromoPins } from '../../actions/newbuildingPromoFeatures';
import { fetchClusters } from '../../actions/results';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { IRegionSpecialPromo } from '../../types/specialPromos';
import { produceGeoChangeEvent } from '../../utils/events';
import { normalizeRegionId } from '../../utils/region';
import { getAvailableSpecialPromo } from '../../utils/specialPromos';
import { useApplicationContext } from '../ApplicationContext';

const GEO_CHANGE_ACTIONS: TModifier['action'][] = [
  'clearGeo',
  'selectGeo',
  'removeGeo',
  'setBSCenter',
  'setRegion',
  'setVillage',
];

export const FiltersContainer: React.FC = () => {
  const { microfrontendsAPI, config } = useApplicationContext();
  const deviceType = useDeviceType();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector<IApplicationState, IJsonQuery>(state => state.filters.jsonQuery);
  const specialPromos = useSelector<IApplicationState, IRegionSpecialPromo[]>(state => state.specialPromos);
  const queryString = useSelector<IApplicationState, string>(state => state.queryString);
  const [isAwaitingTracking, setIsAwaitingTracking] = React.useState<boolean>(false);
  const isAwaitingGeoUpdate = React.useRef<boolean>(false);
  const specialPromo: IRegionSpecialPromo | undefined = getAvailableSpecialPromo(specialPromos, jsonQuery);

  const experimentalFeatures = React.useMemo(
    () => ({
      onlineBookingEnabled: Boolean(config.get<boolean>('onlineBooking.enabled')),
      searchFlatShareEnabled: Boolean(config.getStrict<boolean>('frontend_search_flat_share_enabled.Enabled')),
    }),
    [config],
  );

  const handleChange = useEventCallback((parameters: IOnChangeParameters) => {
    dispatch(changeJsonQuery({ ...parameters, prevJsonQuery: jsonQuery }));

    if (parameters.appliedModifiers.some(modifier => GEO_CHANGE_ACTIONS.includes(modifier.action))) {
      isAwaitingGeoUpdate.current = true;
      dispatch(fetchTags());

      if (config.get<boolean>('header.asMicrofrontend')) {
        const setRegionModifier = parameters.appliedModifiers.find(modifier => modifier.action === 'setRegion');

        if (setRegionModifier !== undefined && microfrontendsAPI) {
          const regionId = normalizeRegionId(setRegionModifier.arguments[0] as NonEmptyArray<number> | null);

          if (regionId) {
            microfrontendsAPI.header.setRegionId({ regionId });
          }
        }
      } else {
        produceGeoChangeEvent(parameters.appliedModifiers);
      }
    }
  });

  const handleApply = useEventCallback(() => {
    dispatch(fetchTags());
    dispatch(fetchClusters({ updateBounds: isAwaitingGeoUpdate.current, deviceType }));
    dispatch(fetchNewbuildingPromoPins());
    isAwaitingGeoUpdate.current = false;
  });

  const handleApplyClick = React.useCallback(() => {
    setIsAwaitingTracking(true);
  }, []);

  React.useEffect(() => {
    if (isAwaitingTracking) {
      trackApplyClick(queryString);

      setIsAwaitingTracking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  return (
    <Provider
      jsonQuery={jsonQuery}
      specialPromo={specialPromo}
      onChange={handleChange}
      onApply={handleApply}
      onClear={trackClearClick}
      onApplyClick={handleApplyClick}
      onAdvancedFiltersOpen={trackAdvancedFiltersClick}
      features={experimentalFeatures}
    >
      <Filters savedSearch={<SaveSearchButton />} />
    </Provider>
  );
};
